// The authenticated console layout. Desktop: fixed 240px nav rail + top bar. Phone: compact top
// bar with a hamburger (slide-in drawer holds the full nav) and a native-style bottom tab bar for
// the five most-used destinations. Nav items are filtered by can() so the UI never shows what the
// user can't reach.
//
// The drawer is grouped rather than a flat list of twenty links: on a phone an unsectioned column
// that long forces the user to read every label to find one. Groups let a thumb aim at a region.
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { PermissionKey } from '@alignova/shared';
import { useAuth } from '../auth/AuthContext.tsx';
import { AlignovaLockup, AlignovaMark } from '../ui/AlignovaLogo.tsx';
import { useCan } from '../auth/useCan.ts';
import { useAudienceTheme } from '../theme/useAudienceTheme.ts';
import { t, type MessageKey } from '../i18n/index.ts';
import { Button } from '../ui/primitives.tsx';
import { TabBar, useScrollLock, type TabDef } from '../ui/mobile.tsx';
import {
  IconBoard,
  IconBell,
  IconCard,
  IconCases,
  IconChat,
  IconClinic,
  IconInbox,
  IconClipboard,
  IconClose,
  IconGear,
  IconImage,
  IconInvoice,
  IconLedger,
  IconLock,
  IconMenu,
  IconOverview,
  IconPeople,
  IconPin,
  IconPlus,
  IconProduction,
  IconProfile,
  IconReceipt,
  IconRoute,
  IconShield,
  IconSignOut,
  IconTag,
  IconUsers,
} from '../ui/icons.tsx';
import { SyncIndicator } from './SyncIndicator.tsx';

/** Drawer sections, in the order a lab actually works: the floor, then records, then the books. */
type Group = 'work' | 'records' | 'money' | 'admin';
const GROUP_LABELS: Record<Group, string> = {
  work: 'Production',
  records: 'Records',
  money: 'Finance',
  admin: 'Administration',
};

type NavIcon = (p: { size?: number; className?: string }) => ReactElement;

interface NavItem {
  to: string;
  label: MessageKey;
  /** The glyph beside the label in the rail and drawer. */
  icon: NavIcon;
  permission?: PermissionKey;
  group: Group;
  /** Items that only exist to back the phone tab bar are hidden from the drawer and rail. */
  tabOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: '/workflow', label: 'nav.dashboard', icon: IconOverview, permission: 'workflow:read', group: 'work' },
  {
    to: '/workflow/board',
    label: 'nav.workflow', icon: IconBoard,
    permission: 'workflow:read',
    group: 'work',
    tabOnly: true,
  },

  { to: '/chat', label: 'nav.chat', icon: IconChat, group: 'work' },
  {
    to: '/manufacturing',
    label: 'nav.manufacturing', icon: IconProduction,
    permission: 'manufacturing:read',
    group: 'work',
  },
  // Records → Cases is the WORKFLOW case list. The older /cases module reads a different table
  // that this lab never populated, so pointing the nav at it showed staff an empty page and hid
  // the real work one level down. The old module stays routed for deep links; it just isn't
  // advertised any more.
  { to: '/workflow/cases', label: 'nav.cases', icon: IconCases, permission: 'workflow:read', group: 'records' },
  { to: '/clinics', label: 'nav.clinics', icon: IconClinic, permission: 'clinic:read', group: 'records' },
  { to: '/patients', label: 'nav.patients', icon: IconPeople, permission: 'patient:read', group: 'records' },
  { to: '/leads', label: 'nav.leads', icon: IconInbox, permission: 'clinic:manage', group: 'records' },
  {
    to: '/patients/media',
    label: 'nav.mediaReview', icon: IconImage,
    permission: 'patient:review_media',
    group: 'records',
  },
  { to: '/tracking', label: 'nav.fieldTracking', icon: IconPin, permission: 'field:read', group: 'records' },
  { to: '/tracking/team', label: 'nav.fieldTeam', icon: IconRoute, permission: 'field:team', group: 'records' },
  { to: '/tracking/reports', label: 'nav.fieldReports', icon: IconClipboard, permission: 'field:read', group: 'records' },
  { to: '/finance/invoices', label: 'nav.invoices', icon: IconInvoice, permission: 'finance:read', group: 'money' },
  { to: '/finance/payments', label: 'nav.payments', icon: IconCard, permission: 'finance:read', group: 'money' },
  { to: '/accounting/expenses/new', label: 'nav.expenses', icon: IconReceipt, permission: 'expense:manage', group: 'money' },
  { to: '/finance/prices', label: 'nav.priceBook', icon: IconTag, permission: 'finance:read', group: 'money' },
  { to: '/accounting', label: 'nav.accounting', icon: IconLedger, permission: 'accounting:read', group: 'money' },
  { to: '/admin/users', label: 'nav.users', icon: IconUsers, permission: 'user:manage', group: 'admin' },
  {
    to: '/admin/notifications',
    label: 'nav.notifications', icon: IconBell,
    permission: 'notification:read',
    group: 'admin',
  },
  { to: '/admin/settings', label: 'nav.settings', icon: IconGear, permission: 'settings:manage', group: 'admin' },
  { to: '/admin/rbac', label: 'nav.rbac', icon: IconShield, permission: 'rbac:manage', group: 'admin' },
  { to: '/admin/audit', label: 'nav.audit', icon: IconClipboard, permission: 'audit:read', group: 'admin' },
  { to: '/admin/security', label: 'nav.security', icon: IconLock, permission: 'security:read', group: 'admin' },
];

const GROUP_ORDER: Group[] = ['work', 'records', 'money', 'admin'];

/** Up to two initials for the header avatar. Falls back to a dash rather than rendering empty. */
function initials(name: string | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '-';
  return (parts[0]![0]! + (parts[1]?.[0] ?? '')).toUpperCase();
}

export function AppShell() {
  const { user, logout } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  useAudienceTheme(user?.role);
  useScrollLock(open);

  const items = NAV.filter((n) => (!n.permission || can(n.permission)) && !n.tabOnly);

  // Close the drawer on navigation.
  useEffect(() => setOpen(false), [location.pathname]);

  async function onSignOut() {
    await logout();
    navigate('/login');
  }

  const linkCls = ({ isActive }: { isActive: boolean }) =>
    `tap-row mb-0.5 flex items-center gap-2.5 rounded-md px-3 py-2.5 text-[15px] font-medium transition lg:py-2 lg:text-sm ${
      isActive ? 'bg-rail-active text-rail-active-fg shadow-level-1' : 'text-rail-ink hover:bg-rail-2'
    }`;

  const navList = (
    <nav className="flex-1 overflow-y-auto px-3 py-2">
      {GROUP_ORDER.map((g) => {
        const inGroup = items.filter((n) => n.group === g);
        if (!inGroup.length) return null;
        return (
          <div key={g} className="mb-4 last:mb-0">
            <div className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-rail-muted">
              {GROUP_LABELS[g]}
            </div>
            {inGroup.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === '/'} className={linkCls}>
                {({ isActive }) => (
                  <>
                    <item.icon size={18} className={`shrink-0 ${isActive ? 'text-rail-active-fg' : 'text-rail-muted'}`} />
                    <span className="truncate">{t(item.label)}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>
  );

  // The destinations a phone gets; everything else is one tap away in the drawer. Filtered by the
  // same permissions as the rail, so a user without the workflow module isn't given tabs that
  // bounce them off a guard.
  const tabs: TabDef[] = [
    ...(can('workflow:read')
      ? ([
          { to: '/workflow', end: true, label: 'Overview', icon: IconOverview },
          { to: '/workflow/board', label: 'Board', icon: IconBoard },
        ] as TabDef[])
      : ([{ to: '/', end: true, label: 'Home', icon: IconOverview }] as TabDef[])),
    { to: '/chat', label: 'Lab chat', icon: IconChat },
    { label: 'Menu', icon: IconProfile, onClick: () => setOpen(true) },
  ];

  return (
    <div className="flex h-full flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-toast focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-fg"
      >
        Skip to content
      </a>

      {/* ---------- Full-width top bar ---------- */}
      <header className="flex shrink-0 items-center gap-3 border-b border-line bg-surface px-2 py-2 pt-safe lg:px-4 lg:py-2.5">
        <button
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          className="tap grid h-10 w-10 shrink-0 place-items-center rounded-lg text-ink hover:bg-surface-2 lg:hidden"
        >
          <IconMenu />
        </button>

        {/* Brand: the lockup on a desk, mark + wordmark on a phone. It lives here now rather than
            at the top of the rail, so the bar reads as the application's own chrome. */}
        <span className="hidden lg:block lg:w-56">
          <AlignovaLockup sub="Clear Aligner Lab" size="sm" />
        </span>
        <span className="flex items-center gap-1.5 lg:hidden" style={{ color: 'var(--c-primary)' }}>
          <AlignovaMark className="h-6 w-6" />
          <span className="text-[13px] font-extrabold tracking-[0.16em]">ALIGNOVA</span>
        </span>

        {/* The role is context, not a heading: a quiet chip rather than wide-tracked mono. */}
        <span className="ml-2 hidden rounded-full border border-line px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted lg:inline-block">
          {user?.role.replace('_', '-')}
        </span>

        <div className="ml-auto flex items-center gap-2 lg:gap-3">
          <SyncIndicator />
          <span className="hidden items-center gap-2 lg:flex">
            <span className="grid h-8 w-8 place-items-center rounded-full bg-surface-2 text-[11px] font-bold uppercase text-muted">
              {initials(user?.displayName)}
            </span>
            <span className="max-w-[12rem] truncate text-sm font-medium text-ink">
              {user?.displayName}
            </span>
          </span>
          <button
            type="button"
            onClick={onSignOut}
            title={t('nav.signOut')}
            aria-label={t('nav.signOut')}
            className="hidden h-9 w-9 place-items-center rounded-md border border-transparent text-muted transition hover:bg-surface-2 hover:text-ink lg:grid"
          >
            <IconSignOut size={18} />
          </button>
        </div>
      </header>

      {/* ---------- Rail + content, beneath the bar ---------- */}
      <div className="flex min-h-0 flex-1">
        {/* Desktop rail */}
        <aside className="hidden w-rail shrink-0 flex-col border-r border-rail-line bg-rail lg:flex">
          {navList}
        </aside>

        {/* Phone drawer */}
        {open && (
          <div
            className="fixed inset-0 z-overlay bg-navy/50 animate-fade lg:hidden"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
        )}
        {/* `invisible` when closed does what aria-hidden alone cannot: it takes the twenty nav links
          out of the tab order, so a keyboard user isn't walked through an off-screen menu. */}
        <aside
          className={`fixed inset-y-0 left-0 z-drawer flex w-[84vw] max-w-xs transform flex-col bg-rail pt-safe shadow-level-3 transition-[transform,visibility] duration-200 ease-out lg:hidden ${
            open ? 'visible translate-x-0' : 'invisible -translate-x-full'
          }`}
          aria-hidden={!open}
        >
          <div className="flex items-center justify-between border-b border-rail-line px-4 py-3">
            <AlignovaLockup sub="Clear Aligner Lab" size="sm" />
            <button
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              className="tap grid h-10 w-10 place-items-center rounded-lg text-rail-muted hover:bg-rail-2"
            >
              <IconClose />
            </button>
          </div>
          {navList}
          <div className="hairline-t px-4 py-3 pb-safe">
            <div className="mb-2 flex items-center gap-2.5">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-surface-2 text-muted">
                <IconProfile size={20} />
              </span>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-ink">{user?.displayName}</div>
                <div className="truncate font-mono text-[11px] uppercase tracking-wide text-muted">
                  {user?.role.replace('_', '-')}
                </div>
              </div>
            </div>
            <Button variant="ghost" onClick={onSignOut} block>
              <IconSignOut size={18} />
              {t('nav.signOut')}
            </Button>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <main id="main" className="min-h-0 flex-1 overflow-auto p-3 pb-bar lg:p-6 lg:pb-6">
            <Outlet />
          </main>

          <TabBar
            tabs={tabs}
            action={
              can('workflow:create')
                ? { label: 'New case', icon: IconPlus, onClick: () => navigate('/workflow?new=1') }
                : undefined
            }
          />
        </div>
      </div>
    </div>
  );
}
